/**
 * State-level legislation API – Open States v3 and NY Open Legislation.
 * Mirrors mobile BillsScreen.js data fetching with throttling and caching.
 */

// ── State options (same list as mobile) ──────────────────────────────────────
export const STATE_OPTIONS = [
  { code: "FED", name: "Federal", available: true },
  { code: "NY", name: "New York", available: true, useNYApi: true },
  { code: "AL", name: "Alabama", available: true },
  { code: "AK", name: "Alaska", available: true },
  { code: "AZ", name: "Arizona", available: true },
  { code: "AR", name: "Arkansas", available: true },
  { code: "CA", name: "California", available: true },
  { code: "CO", name: "Colorado", available: true },
  { code: "CT", name: "Connecticut", available: true },
  { code: "DE", name: "Delaware", available: true },
  { code: "FL", name: "Florida", available: true },
  { code: "GA", name: "Georgia", available: true },
  { code: "HI", name: "Hawaii", available: true },
  { code: "ID", name: "Idaho", available: true },
  { code: "IL", name: "Illinois", available: true },
  { code: "IN", name: "Indiana", available: true },
  { code: "IA", name: "Iowa", available: true },
  { code: "KS", name: "Kansas", available: true },
  { code: "KY", name: "Kentucky", available: true },
  { code: "LA", name: "Louisiana", available: true },
  { code: "ME", name: "Maine", available: true },
  { code: "MD", name: "Maryland", available: true },
  { code: "MA", name: "Massachusetts", available: true },
  { code: "MI", name: "Michigan", available: true },
  { code: "MN", name: "Minnesota", available: true },
  { code: "MS", name: "Mississippi", available: true },
  { code: "MO", name: "Missouri", available: true },
  { code: "MT", name: "Montana", available: true },
  { code: "NE", name: "Nebraska", available: true },
  { code: "NV", name: "Nevada", available: true },
  { code: "NH", name: "New Hampshire", available: true },
  { code: "NJ", name: "New Jersey", available: true },
  { code: "NM", name: "New Mexico", available: true },
  { code: "NC", name: "North Carolina", available: true },
  { code: "ND", name: "North Dakota", available: true },
  { code: "OH", name: "Ohio", available: true },
  { code: "OK", name: "Oklahoma", available: true },
  { code: "OR", name: "Oregon", available: true },
  { code: "PA", name: "Pennsylvania", available: true },
  { code: "RI", name: "Rhode Island", available: true },
  { code: "SC", name: "South Carolina", available: true },
  { code: "SD", name: "South Dakota", available: true },
  { code: "TN", name: "Tennessee", available: true },
  { code: "TX", name: "Texas", available: true },
  { code: "UT", name: "Utah", available: true },
  { code: "VT", name: "Vermont", available: true },
  { code: "VA", name: "Virginia", available: true },
  { code: "WA", name: "Washington", available: true },
  { code: "WV", name: "West Virginia", available: true },
  { code: "WI", name: "Wisconsin", available: true },
  { code: "WY", name: "Wyoming", available: true },
  { code: "DC", name: "District of Columbia", available: true },
  { code: "PR", name: "Puerto Rico", available: true },
];

export const STATE_NAMES = Object.fromEntries(STATE_OPTIONS.filter(s => s.code !== "FED").map(s => [s.code, s.name]));

export function getStateName(code) {
  return STATE_NAMES[code] || code;
}

// ── Throttle / cache helpers ─────────────────────────────────────────────────
const CACHE_TTL = 10 * 60 * 1000; // 10 min
const cache = new Map();
let lastRequestTime = 0;
const MIN_INTERVAL = 1100; // 1.1s between Open States requests

function getCached(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) { cache.delete(key); return null; }
  return entry.data;
}
function setCache(key, data) { cache.set(key, { data, ts: Date.now() }); }

async function throttledFetch(url, options, retries = 3) {
  for (let attempt = 0; attempt < retries; attempt++) {
    const now = Date.now();
    const wait = Math.max(0, MIN_INTERVAL - (now - lastRequestTime));
    if (wait > 0) await new Promise(r => setTimeout(r, wait));
    lastRequestTime = Date.now();

    const res = await fetch(url, options);
    if (res.status === 429) {
      const delay = Math.min(2000 * Math.pow(2, attempt), 15000);
      if (attempt < retries - 1) { await new Promise(r => setTimeout(r, delay)); continue; }
      throw new Error("Rate limit reached. Please try again in a moment.");
    }
    return res;
  }
  throw new Error("Request failed after retries");
}

// ── Transform helpers (match mobile shapes) ──────────────────────────────────
function transformOpenStatesBill(bill, stateCode) {
  const primary = bill.sponsorships?.find(s => s.primary) || bill.sponsorships?.[0];
  const parts = bill.identifier?.split(" ") || [];
  const billType = parts[0] || "";
  const billNumber = parts.slice(1).join(" ") || bill.identifier;
  return {
    id: bill.id,
    bill_number: billNumber,
    title: bill.title || "No title available",
    summary: bill.abstract || "",
    type: billType,
    sponsor: primary?.name || "Unknown",
    sponsor_name: primary?.name || "Unknown",
    status: bill.latest_action_description || "Unknown",
    session: bill.session,
    state: stateCode,
    isStateBill: true,
    useOpenStates: true,
    origin_chamber: billType.startsWith("H") ? "House" : billType.startsWith("S") ? "Senate" : billType.startsWith("A") ? "Assembly" : "Legislature",
    openStatesData: {
      id: bill.id,
      jurisdiction: bill.jurisdiction,
      session: bill.session,
      identifier: bill.identifier,
      openstates_url: bill.openstates_url,
      latest_action_date: bill.latest_action_date,
    },
  };
}

// ── Open States list ─────────────────────────────────────────────────────────
export async function fetchOpenStatesBills(stateCode, searchQuery = "", page = 1) {
  const apiKey = process.env.REACT_APP_OPEN_STATES_API_KEY;
  if (!apiKey) return { bills: [], totalCount: 0, error: "Open States API key not configured" };

  const cacheKey = `os_bills_${stateCode}_${searchQuery}_${page}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const jurisdiction = stateCode.toLowerCase();
  let url = `https://v3.openstates.org/bills?jurisdiction=${jurisdiction}&per_page=15&page=${page}&sort=latest_action_desc&include=sponsorships`;
  if (searchQuery.trim()) url += `&q=${encodeURIComponent(searchQuery)}`;

  try {
    const res = await throttledFetch(url, { headers: { "X-API-KEY": apiKey } });
    if (!res.ok) return { bills: [], totalCount: 0, error: `API error: ${res.status}` };
    const data = await res.json();
    const result = {
      bills: (data.results || []).map(b => transformOpenStatesBill(b, stateCode)),
      totalCount: data.pagination?.total_items || 0,
    };
    setCache(cacheKey, result);
    return result;
  } catch (err) {
    return { bills: [], totalCount: 0, error: err.message || "Failed to load state bills." };
  }
}

// ── Open States detail ───────────────────────────────────────────────────────
export async function fetchOpenStatesBillDetail(openStatesData, stateCode) {
  const apiKey = process.env.REACT_APP_OPEN_STATES_API_KEY;
  if (!apiKey) throw new Error("Open States API key not configured");

  const { id, session, identifier } = openStatesData;
  const cacheKey = `os_detail_${id}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  let url;
  if (id?.startsWith("ocd-bill/")) {
    url = `https://v3.openstates.org/bills/${encodeURIComponent(id)}?include=sponsorships&include=abstracts&include=actions&include=votes`;
  } else {
    url = `https://v3.openstates.org/bills/${stateCode.toLowerCase()}/${session}/${encodeURIComponent(identifier)}?include=sponsorships&include=abstracts&include=actions&include=votes`;
  }

  const res = await throttledFetch(url, { headers: { "X-API-KEY": apiKey } });
  if (!res.ok) {
    if (res.status === 404) throw new Error("Bill not found.");
    throw new Error(`Failed to fetch bill: ${res.status}`);
  }
  const bill = await res.json();
  const primary = bill.sponsorships?.find(s => s.primary) || bill.sponsorships?.[0];
  const cosponsors = (bill.sponsorships || []).filter(s => !s.primary).map(s => ({
    first_name: s.name?.split(" ")[0] || "",
    last_name: s.name?.split(" ").slice(1).join(" ") || "",
    party: s.party || null,
  }));
  const parts = bill.identifier?.split(" ") || [];
  const billType = parts[0] || "";
  const billNumber = parts.slice(1).join(" ") || bill.identifier;

  const result = {
    id: bill.id,
    bill_number: billNumber,
    title: bill.title || "No title available",
    summary: bill.abstracts?.[0]?.abstract || "",
    type: billType,
    sponsor_name: primary?.name || "Unknown",
    sponsor_party: primary?.party || null,
    status: bill.latest_action_description || "Unknown",
    session: bill.session,
    congress: bill.session,
    policy_area: bill.subject?.[0] || null,
    isStateBill: true,
    stateCode,
    stateName: getStateName(stateCode),
    dataSource: "Open States",
    origin_chamber: billType.startsWith("H") ? "House" : billType.startsWith("S") ? "Senate" : "Legislature",
    xml_txt_url: bill.openstates_url || null,
    openstates_url: bill.openstates_url,
    cosponsors,
    actions: bill.actions || [],
    votes: bill.votes || [],
    latest_action_date: bill.latest_action_date,
  };
  setCache(cacheKey, result);
  return result;
}

// NY list/search: most recent legislative activity first (API default is oldest-first without this).
const NY_BILLS_SORT = "status.actionDate:DESC";

export function getNyDefaultSessionYear(date = new Date()) {
  const currentYear = date.getFullYear();
  return currentYear % 2 === 0 ? currentYear - 1 : currentYear;
}

function nyBillsListUrl({ sessionYear, searchQuery, limit, offset, apiKey }) {
  const sort = `&sort=${encodeURIComponent(NY_BILLS_SORT)}`;
  if (searchQuery.trim()) {
    return (
      `https://legislation.nysenate.gov/api/3/bills/search?term=${encodeURIComponent(searchQuery)}` +
      `&limit=${limit}&offset=${offset}${sort}&key=${apiKey}`
    );
  }
  return (
    `https://legislation.nysenate.gov/api/3/bills/${sessionYear}?limit=${limit}&offset=${offset}${sort}&key=${apiKey}`
  );
}

// ── NY Open Legislation list ─────────────────────────────────────────────────
export async function fetchNYBills(searchQuery = "", page = 1) {
  const apiKey = process.env.REACT_APP_NY_OPEN_LEGISLATION_API_KEY;
  if (!apiKey) return { bills: [], totalCount: 0, error: "NY Open Legislation API key not configured" };

  const limit = 15;
  const offset = (page - 1) * limit;
  const sessionYear = getNyDefaultSessionYear();

  const cacheKey = `ny_bills_${NY_BILLS_SORT}_${searchQuery}_${page}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const url = nyBillsListUrl({ sessionYear, searchQuery, limit, offset, apiKey });

  try {
    const res = await throttledFetch(url, {});
    const data = await res.json();
    if (!data.success) return { bills: [], totalCount: 0, error: data.message || "NY API error" };

    const bills = (data.result?.items || []).map(item => {
      const b = item.result || item;
      return {
        id: `ny-${b.basePrintNo}-${b.session}`,
        bill_number: b.printNo || b.basePrintNo,
        title: b.title || "No title available",
        summary: b.summary || "",
        type: b.billType?.chamber === "SENATE" ? "S" : "A",
        sponsor: b.sponsor?.member?.fullName || "Unknown",
        sponsor_name: b.sponsor?.member?.fullName || "Unknown",
        status: b.status?.statusDesc || "Unknown",
        session: b.session,
        state: "NY",
        isStateBill: true,
        useNYApi: true,
        origin_chamber: b.billType?.chamber === "SENATE" ? "Senate" : "Assembly",
        nyData: { basePrintNo: b.basePrintNo, session: b.session, activeVersion: b.activeVersion },
      };
    });
    const result = { bills, totalCount: data.total || bills.length };
    setCache(cacheKey, result);
    return result;
  } catch (err) {
    return { bills: [], totalCount: 0, error: err.message || "Failed to load NY bills." };
  }
}

// ── NY Open Legislation detail ───────────────────────────────────────────────
export async function fetchNYBillDetail(nyData) {
  const apiKey = process.env.REACT_APP_NY_OPEN_LEGISLATION_API_KEY;
  if (!apiKey) throw new Error("NY Open Legislation API key not configured");

  const { basePrintNo, session } = nyData;
  const cacheKey = `ny_detail_${session}_${basePrintNo}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const url = `https://legislation.nysenate.gov/api/3/bills/${session}/${basePrintNo}?key=${apiKey}`;
  const res = await throttledFetch(url, {});
  const data = await res.json();
  if (!data.success) throw new Error(data.message || "Failed to fetch NY bill");

  const b = data.result;
  const cosponsors = (b.amendments?.items?.[""]?.coSponsors?.items || []).map(m => ({
    first_name: m.fullName?.split(" ")[0] || "",
    last_name: m.fullName?.split(" ").slice(1).join(" ") || "",
    party: null,
  }));

  const result = {
    id: `ny-${b.basePrintNo}-${b.session}`,
    bill_number: b.printNo || b.basePrintNo,
    title: b.title || "No title available",
    summary: b.summary || "",
    type: b.billType?.chamber === "SENATE" ? "S" : "A",
    sponsor_name: b.sponsor?.member?.fullName || "Unknown",
    sponsor_party: null,
    status: b.status?.statusDesc || "Unknown",
    session: b.session,
    congress: b.session,
    policy_area: b.amendments?.items?.[""]?.lawSection || null,
    isStateBill: true,
    stateCode: "NY",
    stateName: "New York",
    dataSource: "NY Open Legislation",
    origin_chamber: b.billType?.chamber === "SENATE" ? "Senate" : "Assembly",
    xml_txt_url: null,
    memo: b.amendments?.items?.[b.activeVersion]?.memo || "",
    cosponsors,
  };
  setCache(cacheKey, result);
  return result;
}

// ── Helpers for bill routing ─────────────────────────────────────────────────
export function buildStateBillPath(bill) {
  if (!bill?.isStateBill) return null;
  const id = encodeURIComponent(bill.id || `${bill.state}-${bill.bill_number}`);
  return `/bills/state/${bill.state}/${id}`;
}

export function getStateBillHref(bill) {
  if (!bill) return "";
  if (bill.stateCode === "NY" || bill.state === "NY") {
    return `https://legislation.nysenate.gov/legislation/bills/${bill.session}/${bill.bill_number}`;
  }
  return bill.openstates_url || bill.openStatesData?.openstates_url || `https://openstates.org/${(bill.stateCode || bill.state || "").toLowerCase()}/bills/${bill.session}/${bill.bill_number}`;
}

export function getStateBillSourceName(bill) {
  if (bill?.stateCode === "NY" || bill?.state === "NY") return "NY Senate Website";
  return "Open States";
}

export function getDataSourceAttribution(bill) {
  if (bill?.dataSource === "NY Open Legislation") return "NY Senate Open Legislation API";
  if (bill?.dataSource === "Open States") return "Open States API";
  return null;
}
